import {compressToBase64, decompressFromBase64} from 'lz-string';
import {JSON_INDENT_SIZE} from '../constants';

const ENCODING_VERSION = 'lz-base64-json-v1';

type EncodedPayload = {
	__visualSelectEncoding: typeof ENCODING_VERSION;
	data: string;
};

const isObject = (value: unknown): value is Record<string, unknown> => {
	return typeof value === 'object' && value !== null;
};

const isEncodedPayload = (value: unknown): value is EncodedPayload => {
	if (!isObject(value)) {
		return false;
	}

	return value.__visualSelectEncoding === ENCODING_VERSION
		&& typeof value.data === 'string';
};

const decodeStoredJson = (storedValue: unknown): string | null => {
	if (typeof storedValue !== 'string') {
		return null;
	}

	let parsed: unknown;

	try {
		parsed = JSON.parse(storedValue) as unknown;
	} catch {
		return null;
	}

	if (!isEncodedPayload(parsed)) {
		return storedValue;
	}

	const decompressed = decompressFromBase64(parsed.data);

	if (decompressed === null) {
		return null;
	}

	return decompressed;
};

const normalizeJson = (rawJson: string): string => JSON.stringify(JSON.parse(rawJson));

const serializeParameterJson = (rawJson: string): string => {
	const normalizedJson = normalizeJson(rawJson);
	const compressedJson = compressToBase64(normalizedJson);

	if (!compressedJson) {
		return normalizedJson;
	}

	const encodedPayload = JSON.stringify({
		__visualSelectEncoding: ENCODING_VERSION,
		data: compressedJson,
	} as EncodedPayload);

	return encodedPayload.length < normalizedJson.length
		? encodedPayload
		: normalizedJson;
};

const formatForEditor = (storedValue: unknown, fallbackJson: string): string => {
	const decoded = decodeStoredJson(storedValue);

	if (decoded === null) {
		return fallbackJson;
	}

	try {
		return JSON.stringify(JSON.parse(decoded), null, JSON_INDENT_SIZE);
	} catch {
		return fallbackJson;
	}
};

const parseParameterJson = <T>(storedValue: unknown, fallback: T): T => {
	const decoded = decodeStoredJson(storedValue);

	if (decoded === null) {
		return fallback;
	}

	try {
		return JSON.parse(decoded) as T;
	} catch {
		return fallback;
	}
};

export {
	formatForEditor,
	parseParameterJson,
	serializeParameterJson,
};
